"use client";

import { useCompletion } from "@ai-sdk/react";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useRouter } from "next/navigation";
import { saveItineraryEdit } from "@/app/lib/saveItineraryEdit";
import { fixEncodedLinks } from "@/app/lib/itinerary/fixLinks";

type UpdateItNewProps = {
  city: string;
  itinid: string;
  updateinstruction?: string;
};

export default function UpdateItNew({ city, itinid }: UpdateItNewProps) {
  const placeholderText = [
    "Make it a 2 day trip",
    "Add more food locations",
    "Make it more outdoorsy",
    "Respond in Hindi",
  ];

  const [index, setIndex] = useState(0);
  const [itineraryUpdated, setItineraryUpdated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const router = useRouter();

  const apiBody = { city: city };

  const {
    completion,
    input,
    isLoading,
    handleInputChange,
    handleSubmit,
    setInput,
  } = useCompletion({
    api: "/api/plan",
    body: apiBody,
    onFinish: () => {
      setIsSubmitting(false);
      setItineraryUpdated(true);
    },
  });

  const handleUpdateBtnClick = () => {
    if (input) {
      setIsSubmitting(true);

      // Ensure the form submission happens explicitly
      setTimeout(() => {
        (
          document.getElementById(
            "UpdateItineraryForm"
          ) as HTMLFormElement | null
        )?.requestSubmit();
      }, 0);

      // @ts-expect-error - gtag might not be defined in TypeScript
      window.gtag?.("event", "update_itinerary", {
        event_category: "update_itinerary",
        event_label: "update_itinerary",
        value: input,
      });
    }
  };

  const handlePromptClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const target = event.target as HTMLButtonElement;
    if (target.textContent) {
      setIsSubmitting(true);
      setInput(target.textContent);

      // Ensure handleSubmit runs after input is set
      setTimeout(() => {
        (
          document.getElementById(
            "UpdateItineraryForm"
          ) as HTMLFormElement | null
        )?.requestSubmit();
      }, 0);
      // @ts-expect-error - gtag might not be defined in TypeScript
      window.gtag?.("event", "update_itinerary", {
        event_category: "update_itinerary",
        event_label: "update_itinerary",
        value: target.textContent,
      });
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prevIndex) =>
        prevIndex === placeholderText.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (completion) {
      const itineraryOutput = document.getElementById("itineraryOutput");
      if (itineraryOutput) {
        itineraryOutput.innerHTML = fixEncodedLinks(completion);
      }
    }
  }, [completion]);

  useEffect(() => {
    if (!itineraryUpdated) return;
    // Persist via the shared helper (mints a new UUID for DEFAULT-slot edits so
    // they don't pollute the plain city page — see saveItineraryEdit).
    saveItineraryEdit({ city, itinid, itinerary: completion })
      .then(({ newItinId }) => {
        if (newItinId) {
          // Delay before navigating so the persistence write settles.
          setTimeout(() => router.replace(`/show/${city}/${newItinId}`), 5000);
        }
      })
      .catch((error) => console.error("Error saving itinerary:", error));
  }, [itineraryUpdated]);

  const cityName = decodeURIComponent(city)
    .split(/\s|-/) // Split on spaces or hyphens
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize properly
    .join(" ");

  return (
    <div className="flex flex-col items-center w-full max-w-3xl mx-auto px-4 py-6 bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full"
      >
        <h3 className="text-xl font-extrabold text-gray-900 mb-4">
          Customize Your {cityName} Experience
        </h3>
        <form
          className="flex items-center w-full text-gray-700"
          onSubmit={handleSubmit}
          id="UpdateItineraryForm"
          name="UpdateItineraryForm"
        >
          <div className="relative w-full">
            <input
              placeholder={placeholderText[index]}
              name="updateitineraryinput"
              className="w-full py-3 px-4 pr-12 bg-white border border-gray-300 rounded-l-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ease-in-out"
              id="updateitineraryinput"
              value={input}
              onChange={handleInputChange}
              disabled={isLoading || isSubmitting}
            />
          </div>
          <button
            className="flex items-center justify-center bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white rounded-r-lg px-5 py-3 transition-all duration-200 disabled:opacity-50 hover:cursor-pointer disabled:cursor-not-allowed shadow-md hover:shadow-lg"
            type="submit"
            onClick={handleUpdateBtnClick}
            disabled={isLoading || isSubmitting}
            aria-label="Update Itinerary"
          >
            {isLoading || isSubmitting ? (
              <svg
                className="animate-spin w-6 h-6"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  className="fill-current"
                  d="M10.14,1.16a11,11,0,0,0-9,8.92A1.59,1.59,0,0,0,2.46,12,1.52,1.52,0,0,0,4.11,10.7a8,8,0,0,1,6.66-6.61A1.42,1.42,0,0,0,12,2.69h0A1.57,1.57,0,0,0,10.14,1.16Z"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
                viewBox="0 -960 960 960"
                fill="currentColor"
              >
                <path d="M172-238v-484l574 242-574 242Zm24-36.667L684-480 196-686v165.333L376.667-480 196-440v165.333Zm0 0V-686-274.667Z" />
              </svg>
            )}
          </button>
        </form>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="flex flex-wrap justify-center gap-2 mt-6"
      >
        <p className="w-full text-center text-sm text-gray-600 mb-2">
          Quick suggestions
        </p>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-gradient-to-r from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:cursor-pointer shadow-sm border border-orange-200"
          type="submit"
          form="UpdateItineraryForm"
          onClick={handlePromptClick}
          disabled={isLoading || isSubmitting}
        >
          <span className="flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" />
            </svg>
            Make it a 5 day trip
          </span>
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-gradient-to-r from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:cursor-pointer shadow-sm border border-orange-200"
          type="submit"
          form="UpdateItineraryForm"
          onClick={handlePromptClick}
          disabled={isLoading || isSubmitting}
        >
          <span className="flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" />
            </svg>
            Make it a 2 day trip
          </span>
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-gradient-to-r from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:cursor-pointer shadow-sm border border-orange-200"
          type="submit"
          form="UpdateItineraryForm"
          onClick={handlePromptClick}
          disabled={isLoading || isSubmitting}
        >
          <span className="flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M7 2a1 1 0 011 1v1h3a1 1 0 110 2H9.5v1.5a1 1 0 11-2 0V6H5a1 1 0 110-2h2.5V3a1 1 0 011-1zm9 9a1 1 0 01-1 1h-1v1.5a1 1 0 01-2 0V12h-1.5a1 1 0 010-2H12v-1.5a1 1 0 012 0V10h1.5a1 1 0 011 1z"
                clipRule="evenodd"
              />
            </svg>
            Respond in Hindi
          </span>
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-gradient-to-r from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:cursor-pointer shadow-sm border border-orange-200"
          type="submit"
          form="UpdateItineraryForm"
          onClick={handlePromptClick}
          disabled={isLoading || isSubmitting}
        >
          <span className="flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M6 3a1 1 0 011-1h.01a1 1 0 010 2H7a1 1 0 01-1-1zm2 3a1 1 0 00-2 0v1a2 2 0 00-2 2v1a2 2 0 002 2h6a2 2 0 002-2v-1a2 2 0 00-2-2V6a1 1 0 10-2 0v1H8V6zm10 7a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM7 13a1 1 0 011-1h.01a1 1 0 110 2H8a1 1 0 01-1-1zm-3 3a1 1 0 00-1 1v1a1 1 0 001 1h14a1 1 0 001-1v-1a1 1 0 00-1-1H4z"
                clipRule="evenodd"
              />
            </svg>
            Make it a foodie trip
          </span>
        </motion.button>
      </motion.div>

      {isSubmitting && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-6 text-center text-sm text-gray-600"
        >
          <p>Updating your itinerary...</p>
          <div className="flex justify-center mt-2">
            <div
              className="w-2 h-2 bg-orange-500 rounded-full mr-1 animate-bounce"
              style={{ animationDelay: "0ms" }}
            ></div>
            <div
              className="w-2 h-2 bg-orange-500 rounded-full mr-1 animate-bounce"
              style={{ animationDelay: "150ms" }}
            ></div>
            <div
              className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"
              style={{ animationDelay: "300ms" }}
            ></div>
          </div>
        </motion.div>
      )}

      {itineraryUpdated && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          Itinerary successfully updated!
        </motion.div>
      )}
    </div>
  );
}
